import React from 'react'
import { useLocation } from 'react-router'
import { throttle } from '@mobi/utils/functions'
import {
  cleanUpScrollRestoreOverrideData,
  getScrollRestoreOverride,
  hasScrollRestoreOverride,
  setScrollRestoreOverride,
} from '../helpers/scrollRestoreOverride'

/** Manual back navigation scroll restoration for route.
 *  ONLY Use in top level components
 */
export const useScrollRestoreOverride = ({
  hasPageLoaded,
  customScrolledElement,
  shouldOnlyScrollOnBackNav,
}: {
  hasPageLoaded: boolean
  customScrolledElement?: React.RefObject<HTMLDivElement | null>
  shouldOnlyScrollOnBackNav?: boolean
}) => {
  const location = useLocation()

  React.useEffect(() => cleanUpScrollRestoreOverrideData, [])

  // Set scroll position on mount, and capture position for route key on scroll
  React.useEffect(() => {
    if (!hasPageLoaded) return

    const key = location.key
    const customEl = customScrolledElement?.current
    const scrollElement: Window | HTMLElement = customEl ?? window

    const isBackNav = hasScrollRestoreOverride(key)
    const top = (isBackNav && getScrollRestoreOverride(key)) || 0
    const shouldAllowScroll = (shouldOnlyScrollOnBackNav && isBackNav) || !shouldOnlyScrollOnBackNav
    if (shouldAllowScroll) scrollElement.scrollTo({ top, behavior: 'instant' })

    const watchScroll = () => {
      const offset = customEl ? customEl.scrollTop : window.pageYOffset
      setScrollRestoreOverride(key, offset)
    }
    const watchScrollThrottled = throttle(watchScroll, 200)
    scrollElement.addEventListener('scroll', watchScrollThrottled)

    return () => {
      watchScrollThrottled.cancel()
      scrollElement.removeEventListener('scroll', watchScrollThrottled)
    }
  }, [location.key, customScrolledElement, hasPageLoaded, shouldOnlyScrollOnBackNav])

  return hasScrollRestoreOverride(location.key)
}
